import { useEffect, useState } from "react";
import { ShippingRatesEditor, WeightsTable } from "@/components/admin/WeightsShipping";
import { AdminMessages } from "@/components/admin/AdminMessages";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { Package, Plus, PencilSimple, Trash, X, Receipt, CurrencyDollar, ChartLine, Warning, FilePdf, Printer, MagnifyingGlass, EnvelopeSimple, CheckCircle, DownloadSimple, Bell, ArrowCounterClockwise } from "@phosphor-icons/react";
import { carrierTrackingUrl } from "@/lib/tracking";
import { VialImage } from "@/components/VialImage";

const LOW_STOCK = 10;

const EMPTY = { name: "", sequence: "", molecular_weight: "", cas_number: "", purity: "≥99%", category: "Peptides", price: "", compare_at_price: "", bundle_items: "", description: "", image_url: "", stock: 100, active: true, top_seller: false, weight_oz: "" };

function ProductForm({ initial, onClose, onSaved }) {
  const [form, setForm] = useState({ ...EMPTY, ...initial, bundle_items: Array.isArray(initial?.bundle_items) ? initial.bundle_items.join(", ") : (initial?.bundle_items || ""), compare_at_price: initial?.compare_at_price ?? "", weight_oz: initial?.weight_oz ?? "" });
  const [saving, setSaving] = useState(false);
  const [coa, setCoa] = useState({ path: initial?.coa_path, filename: initial?.coa_filename });
  const [coaBusy, setCoaBusy] = useState(false);
  const [errors, setErrors] = useState({});
  const [confirmAdd, setConfirmAdd] = useState(false);
  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setErrors((e) => (e[k] ? { ...e, [k]: undefined } : e)); };

  const validate = () => {
    const e = {};
    const price = parseFloat(form.price);
    const stock = parseInt(form.stock);
    if (!String(form.name).trim()) e.name = "Name is required";
    if (form.price === "" || form.price == null || isNaN(price) || price <= 0) e.price = "Enter a price greater than 0";
    if (form.stock === "" || form.stock == null || isNaN(stock) || stock < 0) e.stock = "Enter stock (0 or more)";
    if (form.compare_at_price !== "" && form.compare_at_price != null) {
      const cap = parseFloat(form.compare_at_price);
      if (isNaN(cap) || cap <= 0) e.compare_at_price = "Must be a positive number";
      else if (!isNaN(price) && cap <= price) e.compare_at_price = "Should be higher than the price";
    }
    if (form.weight_oz !== "" && form.weight_oz != null && (isNaN(parseFloat(form.weight_oz)) || parseFloat(form.weight_oz) <= 0)) e.weight_oz = "Must be a positive number";
    if (String(form.image_url).trim() && !/^https?:\/\/.+/i.test(String(form.image_url).trim())) e.image_url = "Must start with http:// or https://";
    return e;
  };

  const uploadCoa = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("COA must be a PDF"); return;
    }
    setCoaBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/admin/products/${initial.id}/coa`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setCoa({ path: data.coa_path, filename: data.coa_filename });
      toast.success("COA uploaded");
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setCoaBusy(false); }
  };

  const removeCoa = async () => {
    setCoaBusy(true);
    try {
      await api.delete(`/admin/products/${initial.id}/coa`);
      setCoa({ path: null, filename: null });
      toast.success("COA removed");
    } catch { toast.error("Failed to remove"); }
    finally { setCoaBusy(false); }
  };

  const doSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        price: parseFloat(form.price) || 0,
        stock: parseInt(form.stock) || 0,
        compare_at_price: form.compare_at_price ? parseFloat(form.compare_at_price) : null,
        weight_oz: form.weight_oz !== "" && form.weight_oz != null ? parseFloat(form.weight_oz) : null,
        bundle_items: form.bundle_items ? form.bundle_items.split(",").map((s) => s.trim()).filter(Boolean) : [],
      };
      if (initial?.id) await api.put(`/admin/products/${initial.id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success(initial?.id ? "Product updated" : "Product added to catalog");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const save = () => {
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length) { toast.error("Please fix the highlighted fields"); return; }
    if (!initial?.id) { setConfirmAdd(true); return; }
    doSave();
  };

  const field = (k, label, type = "text") => (
    <div>
      <label className="label-mono text-muted-foreground block mb-1">{label}</label>
      <input data-testid={`pf-${k}`} type={type} value={form[k]} onChange={(e) => set(k, e.target.value)}
        aria-invalid={!!errors[k]}
        className={`w-full bg-transparent border px-3 py-2 text-sm outline-none transition-colors ${errors[k] ? "border-destructive focus:border-destructive" : "border-black/15 focus:border-primary"}`} style={{ transition: "border-color 0.2s" }} />
      {errors[k] && <p data-testid={`pf-error-${k}`} className="text-destructive text-[0.65rem] mt-1">{errors[k]}</p>}
    </div>
  );

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto">
      <div className="bg-surface border border-black/10 w-full max-w-2xl p-8 my-8">
        <div className="flex justify-between items-center mb-6">
          <h2 className="font-head text-2xl">{initial?.id ? "Edit" : "New"} Product</h2>
          <button data-testid="pf-close" onClick={onClose} className="text-muted-foreground hover:text-foreground"><X size={22} /></button>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          {field("name", "Name")}
          {field("category", "Category")}
          {field("sequence", "Sequence")}
          {field("molecular_weight", "Molecular Weight")}
          {field("cas_number", "CAS Number")}
          {field("purity", "Purity")}
          {field("price", "Price (USD)", "number")}
          {field("stock", "Stock", "number")}
          {field("compare_at_price", "Compare-at Price (was, optional)", "number")}
          {field("weight_oz", "Shipping Weight (oz)", "number")}
          <div>
            <label className="label-mono text-muted-foreground block mb-1">Bundle Items (comma separated)</label>
            <input data-testid="pf-bundle_items" type="text" value={form.bundle_items} onChange={(e) => set("bundle_items", e.target.value)}
              placeholder="BPC-157 5mg, TB-500 5mg"
              className="w-full bg-transparent border border-black/15 px-3 py-2 text-sm focus:border-primary outline-none transition-colors" style={{ transition: "border-color 0.2s" }} />
          </div>
          <div className="md:col-span-2">{field("image_url", "Image URL")}</div>
          <div className="md:col-span-2">
            <label className="label-mono text-muted-foreground block mb-1">Description</label>
            <textarea data-testid="pf-description" rows={3} value={form.description} onChange={(e) => set("description", e.target.value)}
              className="w-full bg-transparent border border-black/15 px-3 py-2 text-sm focus:border-primary outline-none transition-colors" style={{ transition: "border-color 0.2s" }} />
          </div>
          <div className="md:col-span-2">
            <button type="button" data-testid="pf-top-seller" onClick={() => set("top_seller", !form.top_seller)}
              className={`flex items-center gap-3 px-4 py-2.5 border transition-colors ${form.top_seller ? "border-primary bg-primary/10 text-primary" : "border-black/15 text-muted-foreground hover:border-black/30"}`}>
              <span className={`w-5 h-5 grid place-items-center border ${form.top_seller ? "bg-primary border-primary text-white" : "border-black/30"}`}>{form.top_seller ? "★" : ""}</span>
              Mark as Top Seller
            </button>
          </div>
        </div>

        <div className="mt-6 border border-black/10 p-4">
          <label className="label-mono text-primary block mb-2">Certificate of Analysis (PDF)</label>
          {initial?.id ? (
            <div>
              {coa.path ? (
                <div className="flex items-center justify-between gap-3">
                  <a data-testid="pf-coa-view" href={`${API}/coa/${initial.id}`} target="_blank" rel="noreferrer" className="text-sm text-primary hover:underline truncate">
                    {coa.filename || "COA.pdf"}
                  </a>
                  <button data-testid="pf-coa-remove" type="button" onClick={removeCoa} disabled={coaBusy} className="label-mono text-muted-foreground hover:text-destructive transition-colors">Remove</button>
                </div>
              ) : (
                <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer hover:text-primary transition-colors">
                  <input data-testid="pf-coa-upload" type="file" accept="application/pdf,.pdf" onChange={uploadCoa} className="hidden" />
                  {coaBusy ? "Uploading…" : "Click to upload a COA PDF"}
                </label>
              )}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">Save the product first, then edit it to attach a COA PDF.</p>
          )}
        </div>

        <div className="sticky bottom-0 bg-surface border-t border-black/10 -mx-8 px-8 -mb-8 pb-8 pt-4 mt-6">
        {confirmAdd ? (
          <div data-testid="pf-confirm-bar" className="border border-primary/40 bg-primary/5 p-4">
            <p className="text-sm mb-3">Add <span className="text-primary font-semibold">{form.name}</span> to your live catalog?</p>
            <div className="flex gap-3">
              <button data-testid="pf-confirm-add" onClick={() => { setConfirmAdd(false); doSave(); }} disabled={saving} className="flex-1 bg-primary text-black label-mono py-3 hover:bg-[#E6C766] transition-colors disabled:opacity-50" style={{ transition: "background-color 0.2s" }}>
                {saving ? "Adding…" : "Yes, add product"}
              </button>
              <button data-testid="pf-confirm-cancel" onClick={() => setConfirmAdd(false)} className="flex-1 border border-black/15 label-mono py-3 hover:border-black/40 transition-colors">Go back</button>
            </div>
          </div>
        ) : (
          <div className="flex gap-3">
            <button data-testid="pf-save" onClick={save} disabled={saving} className="flex-1 bg-primary text-black label-mono py-3 hover:bg-[#E6C766] transition-colors disabled:opacity-50" style={{ transition: "background-color 0.2s" }}>
              {saving ? "Saving…" : (initial?.id ? "Save Product" : "Add Product")}
            </button>
            <button onClick={onClose} className="flex-1 border border-black/15 label-mono py-3 hover:border-black/40 transition-colors">Cancel</button>
          </div>
        )}
        </div>
      </div>
    </div>
  );
}

function OrderTracking({ order, onSave, onResend }) {
  const [tn, setTn] = useState(order.tracking_number || "");
  const [carrier, setCarrier] = useState(order.carrier || "");
  return (
    <div className="mt-3 flex flex-wrap items-center gap-2">
      <select data-testid={`carrier-${order.id}`} value={carrier} onChange={(e) => setCarrier(e.target.value)}
        className="w-32 bg-transparent border border-black/15 px-3 py-1.5 text-sm focus:border-primary outline-none">
        <option value="">Carrier…</option>
        <option value="UPS">UPS</option>
        <option value="USPS">USPS</option>
        <option value="FedEx">FedEx</option>
      </select>
      <input data-testid={`tracking-input-${order.id}`} value={tn} onChange={(e) => setTn(e.target.value)} placeholder="Tracking number"
        className="w-52 bg-transparent border border-black/15 px-3 py-1.5 text-sm focus:border-primary outline-none" />
      <button data-testid={`save-tracking-${order.id}`} onClick={() => onSave(order.id, tn, carrier)}
        className="label-mono px-4 py-1.5 bg-primary text-black hover:bg-[#E6C766] transition-colors" style={{ transition: "background-color 0.2s" }}>
        Save
      </button>
      {order.tracking_number && (
        <>
          {carrierTrackingUrl(order.carrier, order.tracking_number) ? (
            <a data-testid={`track-link-${order.id}`} href={carrierTrackingUrl(order.carrier, order.tracking_number)} target="_blank" rel="noopener noreferrer"
              className="label-mono text-accent text-[0.6rem] underline underline-offset-2 hover:text-primary transition-colors">Track ✓</a>
          ) : (
            <span className="label-mono text-accent text-[0.6rem]">Saved ✓</span>
          )}
          <button data-testid={`resend-tracking-${order.id}`} onClick={() => onResend(order.id)}
            className="label-mono text-[0.6rem] border border-black/15 px-3 py-1.5 text-muted-foreground hover:border-primary hover:text-primary transition-colors inline-flex items-center gap-1">
            <EnvelopeSimple size={12} /> Resend email
          </button>
        </>
      )}
    </div>
  );
}

function ChangePassword() {
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: next });
      toast.success("Password changed");
      setCur(""); setNext(""); setOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to change password"); }
    finally { setBusy(false); }
  };
  if (!open) {
    return (
      <button data-testid="open-change-password" onClick={() => setOpen(true)}
        className="label-mono px-5 py-3 border border-black/15 text-muted-foreground hover:border-primary hover:text-primary transition-colors">
        Change Password
      </button>
    );
  }
  return (
    <div className="flex flex-wrap items-center gap-2">
      <input data-testid="cp-current" type="password" value={cur} onChange={(e) => setCur(e.target.value)} placeholder="Current password"
        className="w-44 bg-transparent border border-black/15 px-3 py-2.5 text-sm focus:border-primary outline-none" />
      <input data-testid="cp-new" type="password" value={next} onChange={(e) => setNext(e.target.value)} placeholder="New password (min 6)"
        className="w-48 bg-transparent border border-black/15 px-3 py-2.5 text-sm focus:border-primary outline-none" />
      <button data-testid="cp-save" onClick={submit} disabled={busy || !cur || next.length < 6}
        className="label-mono px-4 py-2.5 bg-primary text-black hover:bg-[#E6C766] transition-colors disabled:opacity-40">
        {busy ? "Saving…" : "Save"}
      </button>
      <button data-testid="cp-cancel" onClick={() => setOpen(false)} className="label-mono px-3 py-2.5 text-muted-foreground hover:text-destructive transition-colors">Cancel</button>
    </div>
  );
}

const BoxSizeSetting = () => {
  const [box, setBox] = useState({ length: "", width: "", height: "" });
  const [saved, setSaved] = useState(null);
  useEffect(() => {
    api.get("/admin/settings/shipping-box").then(({ data }) => {
      const b = { length: data.length ?? "", width: data.width ?? "", height: data.height ?? "" };
      setBox(b); setSaved(b);
    }).catch(() => {});
  }, []);
  const save = async () => {
    if (JSON.stringify(box) === JSON.stringify(saved)) return;
    const num = (v) => (v === "" || v == null ? null : parseFloat(v));
    try {
      await api.put("/admin/settings/shipping-box", { length: num(box.length), width: num(box.width), height: num(box.height) });
      setSaved(box); toast.success("Default box size saved");
    } catch { toast.error("Box size must be positive numbers"); }
  };
  return (
    <div data-testid="box-size-setting" title="Default box size (inches) added to the Pirate Ship CSV">
      <label className="label-mono text-muted-foreground text-[0.55rem] block mb-1 whitespace-nowrap">Box L×W×H (in)</label>
      <div className="flex items-center gap-1" onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) save(); }}>
        {["length", "width", "height"].map((k) => (
          <input key={k} data-testid={`box-${k}`} type="number" min="0" step="0.1" value={box[k]} placeholder={k[0].toUpperCase()}
            onChange={(e) => setBox((b) => ({ ...b, [k]: e.target.value }))}
            className="w-16 bg-transparent [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none border border-black/15 px-1.5 py-2 text-sm text-center focus:border-primary outline-none transition-colors" />
        ))}
      </div>
    </div>
  );
};

const matchOrderFilter = (o, f) => {
  const open = !["shipped", "delivered"].includes(o.fulfillment_status);
  if (f === "awaiting") return o.payment_status !== "paid" && o.payment_status !== "cancelled";
  if (f === "paid") return o.payment_status === "paid" && open;
  if (f === "ready") return o.payment_status === "paid" && !!o.label_filename && !o.printed_at && open;
  if (f === "shipped") return !open;
  if (f === "cancelled") return o.payment_status === "cancelled";
  return true;
};

const Stat = ({ icon: Icon, label, value }) => (  <div className="border border-black/10 bg-surface p-6">
    <Icon size={22} className="text-primary mb-3" />
    <p className="font-mono text-2xl">{value}</p>
    <p className="label-mono text-muted-foreground text-[0.6rem] mt-1">{label}</p>
  </div>
);

export default function Admin() {
  const [tab, setTab] = useState("products");
  const [productView, setProductView] = useState("catalog");
  const [openMsgs, setOpenMsgs] = useState(0);
  const [msgBannerHidden, setMsgBannerHidden] = useState(() => sessionStorage.getItem("mpc_msg_banner_hidden") === "1");
  const hideMsgBanner = () => { sessionStorage.setItem("mpc_msg_banner_hidden", "1"); setMsgBannerHidden(true); };
  useEffect(() => {
    api.get("/admin/messages").then(({ data }) => setOpenMsgs(data.filter((m) => !m.handled).length)).catch(() => {});
  }, [tab]);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [orderSearch, setOrderSearch] = useState("");
  const [orderFilter, setOrderFilter] = useState("all");
  const [slipDate, setSlipDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [restockCounts, setRestockCounts] = useState({});
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);
  const [selectedOrders, setSelectedOrders] = useState(() => new Set());
  const [printingLabels, setPrintingLabels] = useState(false);
  const [autoShip, setAutoShip] = useState(true);
  const [labelBusy, setLabelBusy] = useState(null);

  const loadProducts = () => api.get("/products").then((r) => setProducts(r.data));
  const loadOrders = () => api.get("/admin/orders").then((r) => setOrders(r.data));
  const loadStats = () => api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  const loadRestock = () => api.get("/admin/restock-counts").then((r) => setRestockCounts(r.data)).catch(() => {});
  useEffect(() => { loadProducts(); loadOrders(); loadStats(); loadRestock(); }, []);

  const del = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/products/${deleteTarget.id}`);
      toast.success("Product deleted");
      setDeleteTarget(null);
      loadProducts();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
    finally { setDeleting(false); }
  };

  const setOrderStatus = async (oid, status) => {
    const { data } = await api.put(`/admin/orders/${oid}/status`, { status });
    toast.success(status === "delivered" && data?.emailed ? "Marked delivered — customer emailed" : "Order updated");
    loadOrders();
  };

  const resendTracking = async (oid) => {
    try {
      const { data } = await api.post(`/admin/orders/${oid}/resend-tracking`);
      toast.success(data?.emailed ? "Shipped email re-sent to customer" : "Could not re-send — no customer email on this order");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not re-send tracking email");
    }
  };

  const saveTracking = async (oid, tracking_number, carrier) => {
    const { data } = await api.put(`/admin/orders/${oid}/tracking`, { tracking_number, carrier });
    toast.success(data?.emailed ? "Tracking saved — customer emailed" : "Tracking saved");
    loadOrders();
  };

  const orderSheetStyles = `
    *{box-sizing:border-box} body{font-family:Arial,Helvetica,sans-serif;color:#1a1a1a;margin:0;padding:40px;}
    .sheet{padding-bottom:24px}
    .sheet + .sheet{page-break-before:always;padding-top:24px}
    .hdr{border-bottom:3px solid #1E1140;padding-bottom:16px;margin-bottom:24px;display:flex;justify-content:space-between;align-items:flex-start}
    .brand{font-size:20px;font-weight:bold;letter-spacing:2px;color:#1E1140}
    .sub{font-size:10px;letter-spacing:2px;color:#888;margin-top:4px}
    .meta{text-align:right;font-size:12px;color:#444;line-height:1.7}
    .cols{display:flex;gap:40px;margin-bottom:24px}
    .col h3{font-size:11px;letter-spacing:1px;text-transform:uppercase;color:#888;margin:0 0 6px}
    .col p{margin:0;font-size:13px;line-height:1.6}
    table{width:100%;border-collapse:collapse;font-size:14px;margin-top:8px}
    td{padding:10px 0;border-bottom:1px solid #eee}
    .tot td{border:none;padding:4px 0}
    .tot .grand td{font-weight:bold;font-size:16px;color:#1E1140;padding-top:10px}
    .ftr{margin-top:36px;padding-top:16px;border-top:1px solid #eee;font-size:10px;color:#999;text-align:center;line-height:1.6}
    @media print{body{padding:20px}}`;

  const orderSheetBody = (o) => {
    const money = (v) => `$${Number(v || 0).toFixed(2)}`;
    const esc = (v) => String(v ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
    const a = o.shipping_address || {};
    const region = [a.city, a.state, a.postal_code].filter(Boolean).join(", ");
    const shipLines = [a.name, a.line1, a.line2, region, a.phone].filter(Boolean).map(esc).join("<br/>") || "<em>No address on file</em>";
    const rows = (o.items || []).map((it) => {
      const unit = it.unit_price ?? it.price ?? 0;
      return `<tr><td>${it.quantity} &times; ${esc(it.name)}</td><td style="text-align:right">${money(unit * it.quantity)}</td></tr>`;
    }).join("");
    const shipping = o.shipping_cost || 0;
    const subtotal = Number(o.amount || 0) - Number(shipping);
    const methodLabels = { zelle: "Zelle", venmo: "Venmo", cashapp: "Cash App", gpay: "Google Pay", applepay: "Apple Pay" };
    return `<div class="sheet">
      <div class="hdr">
        <div><div class="brand">MULAN PEPTIDE COLLECTION</div><div class="sub">RESEARCH USE ONLY · ORDER SHEET</div></div>
        <div class="meta"><b>Order ${esc(o.reference || "")}</b><br/>${new Date(o.created_at).toLocaleString()}<br/>
          Payment: ${esc(o.payment_status || "")} · Fulfillment: ${esc(o.fulfillment_status || "pending")}</div>
      </div>
      <div class="cols">
        <div class="col" style="flex:1"><h3>Ship To</h3><p>${shipLines}</p></div>
        <div class="col" style="flex:1"><h3>Contact & Payment</h3>
          <p>${esc(o.customer_email || "Guest")}<br/>Method: ${esc(methodLabels[o.payment_method] || o.payment_method || "—")}<br/>Shipping: ${esc(o.shipping_method || "standard")}</p>
          ${o.tracking_number ? `<p style="margin-top:8px">Carrier: ${esc(o.carrier || "—")}<br/>Tracking: ${esc(o.tracking_number)}</p>` : ""}
        </div>
      </div>
      <table><tbody>${rows}</tbody></table>
      <table class="tot" style="margin-top:12px;width:280px;margin-left:auto">
        <tr><td>Subtotal</td><td style="text-align:right">${money(subtotal)}</td></tr>
        <tr><td>Shipping (${esc(o.shipping_method || "standard")})</td><td style="text-align:right">${shipping ? money(shipping) : "FREE"}</td></tr>
        <tr class="grand"><td>Total</td><td style="text-align:right">${money(o.amount)} ${esc((o.currency || "usd").toUpperCase())}</td></tr>
      </table>
      <div class="ftr">All products are sold strictly for laboratory and research use only.<br/>&copy; Mulan Peptide Collection LLC</div>
    </div>`;
  };

  const printSheets = (list, title) => {
    if (!list.length) { toast.error("No orders to print"); return; }
    const w = window.open("", "_blank", "width=820,height=1000");
    if (!w) { toast.error("Please allow pop-ups to print"); return; }
    w.document.write(`<!doctype html><html><head><title>${title || "Order"}</title><style>${orderSheetStyles}</style></head><body>${list.map(orderSheetBody).join("")}<script>window.onload=function(){window.print();}</script></body></html>`);
    w.document.close();
  };

  const printOrder = (o) => printSheets([o], `Order ${o.reference || o.id}`);

  const bulkPrintSlips = () => {
    const list = orders.filter((o) =>
      o.payment_status === "paid" &&
      !["shipped", "delivered"].includes(o.fulfillment_status) &&
      (o.created_at || "").slice(0, 10) === slipDate
    );
    if (!list.length) { toast.error("No unshipped paid orders for that date"); return; }
    printSheets(list, `Packing Slips ${slipDate}`);
  };

  const defaultCancelReason = (o) => {
    const soldOut = (o.items || []).some((it) => {
      const p = products.find((pr) => pr.id === it.product_id);
      return p && Number(p.stock) <= 0;
    });
    return soldOut ? "Out of stock" : "";
  };

  const markPaid = async (oid) => {
    await api.put(`/admin/orders/${oid}/mark-paid`);
    toast.success("Order marked as paid"); loadOrders(); loadStats();
  };

  const reinstateOrder = async (oid) => {
    try {
      await api.put(`/admin/orders/${oid}/reinstate`);
      toast.success("Order reinstated — awaiting payment");
      loadOrders(); loadStats();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not reinstate order"); }
  };

  const confirmCancel = async () => {
    if (!cancelTarget) return;
    setCancelling(true);
    try {
      await api.put(`/admin/orders/${cancelTarget.id}/cancel`, { reason: cancelReason.trim() });
      toast.success("Order cancelled");
      setCancelTarget(null); setCancelReason("");
      loadOrders(); loadStats();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not cancel order"); }
    finally { setCancelling(false); }
  };

  const toggleSelectOrder = (oid) => {
    setSelectedOrders((prev) => {
      const next = new Set(prev);
      next.has(oid) ? next.delete(oid) : next.add(oid);
      return next;
    });
  };

  const uploadLabel = async (oid, file) => {
    if (!file) return;
    setLabelBusy(oid);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/orders/${oid}/label`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Shipping label attached");
      loadOrders();
    } catch (e) { toast.error(e.response?.data?.detail || "Label upload failed"); }
    finally { setLabelBusy(null); }
  };

  const removeLabel = async (oid) => {
    try {
      await api.delete(`/admin/orders/${oid}/label`);
      toast.success("Label removed");
      loadOrders();
    } catch (e) { toast.error("Could not remove label"); }
  };

  const printLabelsFor = async (ids, label) => {
    if (!ids.length) { toast.error("Select at least one order"); return; }
    setPrintingLabels(true);
    try {
      const res = await api.post("/admin/orders/print-labels", { order_ids: ids, mark_shipped: autoShip }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const w = window.open(url, "_blank");
      if (!w) { toast.error("Please allow pop-ups to print"); }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      const shipped = Number(res.headers["x-shipped-count"] || 0);
      const failed = (res.headers["x-label-failed"] || "").split(",").filter(Boolean);
      if (failed.length) toast.warning(`Label couldn't be added for ${failed.length} order${failed.length === 1 ? "" : "s"}`, { description: `Only the packing slip printed for: ${failed.join(", ")}. Re-upload the label PDF and print again.`, duration: 12000 });
      toast.success(label || `Prepared ${ids.length} order(s)`, shipped ? { description: `${shipped} order${shipped === 1 ? "" : "s"} marked shipped & customer${shipped === 1 ? "" : "s"} emailed` } : undefined);
      loadOrders();
    } catch (e) {
      const msg = e.response?.status === 404 ? "No printable orders found" : "Could not generate print job";
      toast.error(msg);
    } finally { setPrintingLabels(false); }
  };

  const uploadTrackingCsv = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/orders/tracking-csv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Tracking added to ${data.updated} order${data.updated === 1 ? "" : "s"}`, { description: data.updated ? "Customers are being emailed their tracking link." : undefined });
      if (data.not_found.length) toast.warning(`${data.not_found.length} reference${data.not_found.length === 1 ? "" : "s"} not found`, { description: data.not_found.slice(0, 10).join(", "), duration: 12000 });
      loadOrders();
    } catch (e) { toast.error(e.response?.data?.detail || "Tracking CSV upload failed"); }
  };

  const exportCsv = async (path = "/admin/orders/export", prefix = "mulan-orders", done = "Orders exported") => {
    try {
      const res = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${prefix}-${new Date().toISOString().slice(0, 10)}.csv`;
      const missing = Number(res.headers["x-missing-weight"] || 0);
      if (missing) toast.warning(`${missing} order${missing === 1 ? " has" : "s have"} a product with no weight`, { description: "Weight was left blank for those rows — add product weights in the Products tab.", duration: 10000 });
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(done);
    } catch (e) {
      toast.error("Could not export orders");
    }
  };

  const revenue = orders.filter((o) => o.payment_status === "paid").reduce((s, o) => s + (o.amount || 0), 0);
  const paidCount = orders.filter((o) => o.payment_status === "paid").length;
  const lowStockCount = products.filter((p) => (p.stock ?? 0) <= LOW_STOCK).length;

  return (
    <div className="max-w-7xl mx-auto px-6 py-16">
      {openMsgs > 0 && !msgBannerHidden && tab !== "messages" && (
        <div data-testid="new-messages-banner" className="mb-8 flex items-center justify-between gap-4 flex-wrap border border-primary/60 bg-primary/10 px-5 py-3 animate-in fade-in slide-in-from-top-2 duration-300">
          <span className="inline-flex items-center gap-2 text-sm">
            <EnvelopeSimple size={18} className="text-primary" />
            You have <strong data-testid="new-messages-count" className="text-primary">{openMsgs}</strong> new message{openMsgs === 1 ? "" : "s"}
          </span>
          <div className="flex items-center gap-2">
            <button data-testid="new-messages-view" onClick={() => setTab("messages")} className="label-mono text-[0.6rem] px-4 py-1.5 bg-primary text-black hover:bg-[#E6C766] transition-colors">View</button>
            <button data-testid="new-messages-dismiss" onClick={hideMsgBanner} aria-label="Dismiss" className="p-1.5 text-muted-foreground hover:text-foreground transition-colors"><X size={14} /></button>
          </div>
        </div>
      )}
      <div className="flex justify-between items-center mb-10 gap-4 flex-wrap">
        <div>
          <span className="label-mono text-primary">Control Panel</span>
          <h1 className="font-head text-4xl mt-2">Admin Dashboard</h1>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <ChangePassword />
          {tab === "products" && (
            <button data-testid="new-product-btn" onClick={() => setEditing(EMPTY)} className="flex items-center gap-2 bg-primary text-black label-mono px-5 py-3 hover:bg-[#E6C766] transition-colors" style={{ transition: "background-color 0.2s" }}>
              <Plus size={16} weight="bold" /> New Product
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        <Stat icon={Package} label="Products" value={products.length} />
        <Stat icon={Receipt} label="Total Orders" value={orders.length} />
        <Stat icon={CurrencyDollar} label="Revenue (paid)" value={`$${revenue.toFixed(2)}`} />
        <div className={`border p-6 ${lowStockCount ? "border-destructive/40 bg-destructive/5" : "border-black/10 bg-surface"}`}>
          <Warning size={22} className={lowStockCount ? "text-destructive mb-3" : "text-primary mb-3"} />
          <p className="font-mono text-2xl">{lowStockCount}</p>
          <p className="label-mono text-muted-foreground text-[0.6rem] mt-1">Low Stock (≤{LOW_STOCK})</p>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Stat icon={CurrencyDollar} label="Revenue Today" value={`$${stats.revenue_today.toFixed(2)}`} />
          <Stat icon={ChartLine} label="Revenue (7 days)" value={`$${stats.revenue_week.toFixed(2)}`} />
          <Stat icon={Receipt} label="Awaiting Payment" value={stats.orders_awaiting} />
        </div>
      )}

      {stats && stats.best_sellers?.length > 0 && (
        <div data-testid="best-sellers" className="border border-black/10 bg-surface p-6 mb-10">
          <span className="label-mono text-primary flex items-center gap-2 mb-4"><ChartLine size={16} /> Best Sellers</span>
          <div className="space-y-2">
            {stats.best_sellers.map((b, idx) => (
              <div key={b.name} data-testid={`best-seller-${idx}`} className="flex items-center justify-between text-sm border-b border-black/5 pb-2 last:border-0 last:pb-0">
                <span className="flex items-center gap-3"><span className="font-mono text-muted-foreground w-5">{idx + 1}</span>{b.name}</span>
                <span className="flex items-center gap-6">
                  <span className="label-mono text-muted-foreground text-[0.6rem]">{b.qty} sold</span>
                  <span className="font-mono text-primary w-20 text-right">${b.revenue.toFixed(2)}</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex gap-2 mb-8 border-b border-black/10">
        {["products", "orders", "messages"].map((t) => (
          <button key={t} data-testid={`tab-${t}`} onClick={() => setTab(t)}
            className={`label-mono px-5 py-3 border-b-2 transition-colors ${tab === t ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}
            style={{ transition: "color 0.2s, border-color 0.2s" }}>
            {t}{t === "messages" && openMsgs > 0 && <span data-testid="messages-open-badge" className="ml-2 px-1.5 py-0.5 bg-primary text-black text-[0.55rem]">{openMsgs}</span>}
          </button>
        ))}
      </div>

      {tab === "products" && (
        <div className="flex gap-2 mb-4">
          {[["catalog", "Catalog"], ["weights", "Weights & Shipping"]].map(([k, l]) => (
            <button key={k} data-testid={`products-view-${k}`} onClick={() => setProductView(k)}
              className={`label-mono px-3 py-1.5 border text-[0.6rem] transition-colors ${productView === k ? "border-primary text-primary bg-primary/10" : "border-black/10 text-muted-foreground hover:border-black/30"}`}
              style={{ transition: "color 0.2s, border-color 0.2s" }}>{l}</button>
          ))}
        </div>
      )}
      {tab === "products" && productView === "weights" ? (
        <div className="grid lg:grid-cols-2 gap-6 items-start">
          <WeightsTable products={products} onSaved={loadProducts} />
          <ShippingRatesEditor />
        </div>
      ) : tab === "products" ? (
        <div className="border border-black/10 divide-y divide-black/10">
          {products.map((p) => (
            <div key={p.id} data-testid={`admin-product-${p.id}`} className="flex items-center gap-4 p-4 bg-surface hover:bg-surface-hover transition-colors" style={{ transition: "background-color 0.2s" }}>
              <div className="w-12 h-12 shrink-0 border border-black/10 bg-white">
                <VialImage name={p.name} showName={false} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-head flex items-center gap-2">
                  {p.name}
                  {p.coa_path && <FilePdf size={14} weight="duotone" className="text-primary" title="COA attached" />}
                </p>
                <p className="label-mono text-muted-foreground text-[0.6rem] flex items-center gap-1.5 flex-wrap">
                  <span>{p.category} · Stock {p.stock}</span>
                  {(p.stock ?? 0) <= 0 ? (
                    <span data-testid={`stock-flag-${p.id}`} className="px-1.5 py-0.5 border border-destructive/40 text-destructive bg-destructive/10">OUT OF STOCK</span>
                  ) : (p.stock ?? 0) <= LOW_STOCK ? (
                    <span data-testid={`stock-flag-${p.id}`} className="px-1.5 py-0.5 border border-amber-500/40 text-amber-600 bg-amber-500/10">LOW</span>
                  ) : null}
                  {restockCounts[p.id] > 0 && (
                    <span data-testid={`waitlist-${p.id}`} className="px-1.5 py-0.5 border border-primary/40 text-primary bg-primary/10 inline-flex items-center gap-1"><Bell size={10} weight="fill" /> {restockCounts[p.id]} waiting</span>
                  )}
                </p>
              </div>
              <span className="font-mono text-primary">${p.price?.toFixed(2)}</span>
              <button data-testid={`edit-${p.id}`} onClick={() => setEditing(p)} className="p-2 text-muted-foreground hover:text-primary transition-colors"><PencilSimple size={18} /></button>
              <button data-testid={`delete-${p.id}`} onClick={() => setDeleteTarget(p)} className="p-2 text-muted-foreground hover:text-destructive transition-colors"><Trash size={18} /></button>
            </div>
          ))}
        </div>
      ) : tab === "messages" ? (
        <AdminMessages />
      ) : (
        <div className="space-y-3">
          {orders.length === 0 && <p className="text-muted-foreground font-mono text-sm">No orders yet.</p>}
          {orders.length > 0 && (
            <div className="flex flex-wrap items-end justify-between gap-3 mb-2">
              <div className="relative flex-1 min-w-[220px]">
                <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input data-testid="order-search" value={orderSearch} onChange={(e) => setOrderSearch(e.target.value)}
                  placeholder="Search by email, reference or tracking #"
                  className="w-full bg-transparent border border-black/15 pl-9 pr-3 py-2 text-sm focus:border-primary outline-none transition-colors" />
              </div>
              <div className="flex flex-wrap items-end gap-2 [&>button]:whitespace-nowrap [&>label]:whitespace-nowrap">
                <div>
                  <label className="label-mono text-muted-foreground text-[0.55rem] block mb-1 whitespace-nowrap">Packing slips for</label>
                  <input data-testid="slip-date" type="date" value={slipDate} onChange={(e) => setSlipDate(e.target.value)}
                    className="bg-transparent border border-black/15 px-3 py-2 text-sm focus:border-primary outline-none transition-colors" />
                </div>
                <button data-testid="bulk-print-slips" onClick={bulkPrintSlips}
                  className="label-mono px-4 py-2 bg-primary text-black hover:bg-[#E6C766] transition-colors inline-flex items-center gap-1.5"
                  style={{ transition: "background-color 0.2s" }}><Printer size={14} /> Print Packing Slips</button>
                <label data-testid="tracking-csv-upload" title="Upload Pirate Ship's shipment export (Order ID + Tracking Number) or a CSV with order_reference, tracking_number, carrier (optional)"
                  className="label-mono px-4 py-2 border border-black/15 text-muted-foreground hover:border-primary hover:text-primary transition-colors inline-flex items-center gap-1.5 cursor-pointer"
                  style={{ transition: "color 0.2s, border-color 0.2s" }}>
                  <DownloadSimple size={14} className="rotate-180" /> Tracking CSV
                  <input data-testid="tracking-csv-input" type="file" accept=".csv,text/csv" className="hidden"
                    onChange={(e) => { uploadTrackingCsv(e.target.files?.[0]); e.target.value = ""; }} />
                </label>
                <BoxSizeSetting />
                <button data-testid="export-pirateship" onClick={() => exportCsv("/admin/orders/export-pirateship", "pirateship", "Pirate Ship CSV ready — upload it at pirateship.com › Ship › Import Spreadsheet")}
                  title="Paid, unshipped orders in a spreadsheet Pirate Ship can import"
                  className="label-mono px-4 py-2 border border-black/15 text-muted-foreground hover:border-primary hover:text-primary transition-colors inline-flex items-center gap-1.5"
                  style={{ transition: "color 0.2s, border-color 0.2s" }}><DownloadSimple size={14} /> Pirate Ship CSV</button>
                <button data-testid="export-csv" onClick={() => exportCsv()}
                  className="label-mono px-4 py-2 border border-black/15 text-muted-foreground hover:border-primary hover:text-primary transition-colors inline-flex items-center gap-1.5"
                  style={{ transition: "color 0.2s, border-color 0.2s" }}><DownloadSimple size={14} /> Export CSV</button>
                <label data-testid="auto-ship-toggle" title="Orders with an uploaded label are marked shipped and the customer is emailed"
                  className="label-mono text-[0.6rem] text-muted-foreground inline-flex items-center gap-1.5 py-2 cursor-pointer select-none hover:text-foreground transition-colors">
                  <input data-testid="auto-ship-checkbox" type="checkbox" checked={autoShip} onChange={(e) => setAutoShip(e.target.checked)}
                    className="w-4 h-4 accent-primary cursor-pointer" /> Mark as shipped
                </label>
                <button data-testid="bulk-print-labels" onClick={() => printLabelsFor([...selectedOrders], `Printing ${selectedOrders.size} label${selectedOrders.size === 1 ? "" : "s"} + slip${selectedOrders.size === 1 ? "" : "s"}`)}
                  disabled={selectedOrders.size === 0 || printingLabels}
                  className="label-mono px-4 py-2 border border-accent/40 text-accent bg-accent/10 hover:bg-accent/20 transition-colors inline-flex items-center gap-1.5 disabled:opacity-40 disabled:pointer-events-none"
                  style={{ transition: "background-color 0.2s" }}><Printer size={14} /> {printingLabels ? "Preparing…" : `Labels + Slips${selectedOrders.size ? ` (${selectedOrders.size})` : ""}`}</button>
              </div>
            </div>
          )}
          {orders.length > 0 && (
            <div data-testid="order-filter-tabs" className="flex flex-wrap gap-2 mb-1">
              {[
                { k: "all", label: "All" },
                { k: "awaiting", label: "Awaiting payment" },
                { k: "paid", label: "Paid" },
                { k: "ready", label: "Ready to ship" },
                { k: "shipped", label: "Shipped" },
                { k: "cancelled", label: "Cancelled" },
              ].map((f) => {
                const n = ["awaiting", "paid", "ready"].includes(f.k) ? orders.filter((o) => matchOrderFilter(o, f.k)).length : null;
                return (
                <button key={f.k} data-testid={`order-filter-${f.k}`} onClick={() => setOrderFilter(f.k)}
                  className={`label-mono px-3 py-1.5 border text-[0.6rem] transition-colors ${orderFilter === f.k ? "border-primary text-primary bg-primary/10" : "border-black/10 text-muted-foreground hover:border-black/30"}`}
                  style={{ transition: "color 0.2s, border-color 0.2s" }}>{f.label}{n !== null && <span data-testid={`order-filter-count-${f.k}`}> ({n})</span>}</button>
                );
              })}
            </div>
          )}
          {(() => {
            const q = orderSearch.trim().toLowerCase();
            const matchFilter = (o) => matchOrderFilter(o, orderFilter);
            const shown = orders
              .filter(matchFilter)
              .filter((o) => !q || [o.customer_email, o.reference, o.tracking_number].some((v) => (v || "").toLowerCase().includes(q)));
            if (orders.length > 0 && shown.length === 0) return <p data-testid="order-search-empty" className="text-muted-foreground font-mono text-sm">No orders match your filters.</p>;
            return shown.map((o) => (
            <div key={o.id} data-testid={`admin-order-${o.id}`} className="border border-black/10 bg-surface p-5">
              <div className="flex flex-wrap justify-between gap-3 mb-3">
                <div className="flex items-start gap-3">
                  <input type="checkbox" data-testid={`select-order-${o.id}`}
                    checked={selectedOrders.has(o.id)} onChange={() => toggleSelectOrder(o.id)}
                    className="mt-1 w-4 h-4 accent-primary cursor-pointer" title="Select for label printing" />
                  <div>
                    <p className="font-mono text-sm">{o.customer_email || "Guest"}</p>
                    <p className="label-mono text-muted-foreground text-[0.6rem] mt-1">{new Date(o.created_at).toLocaleString()}{o.reference ? <span data-testid={`order-ref-${o.id}`}> · {o.reference}</span> : null}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`label-mono px-2 py-1 border ${o.payment_status === "paid" ? "border-accent/30 text-accent bg-accent/10" : o.payment_status === "cancelled" ? "border-destructive/30 text-destructive bg-destructive/10" : "border-primary/30 text-primary bg-primary/10"}`}>{o.payment_status}</span>
                  {o.fulfillment_status === "delivered" && (
                    <span data-testid={`delivered-badge-${o.id}`} className="label-mono px-2 py-1 border border-green-500/40 text-green-600 bg-green-500/10 inline-flex items-center gap-1">
                      <CheckCircle size={12} weight="fill" /> Delivered{(o.delivered_at || o.updated_at) ? ` · ${new Date(o.delivered_at || o.updated_at).toLocaleDateString()}` : ""}
                    </span>
                  )}
                  {o.printed_at && (
                    <span data-testid={`printed-badge-${o.id}`} title={`Label + slip last printed ${new Date(o.printed_at).toLocaleString()}`}
                      className="label-mono px-2 py-1 border border-black/20 text-foreground/70 bg-black/5 inline-flex items-center gap-1">
                      <Printer size={12} /> Printed · {new Date(o.printed_at).toLocaleDateString(undefined, { month: "numeric", day: "numeric" })}{o.print_count > 1 ? ` ×${o.print_count}` : ""}
                    </span>
                  )}
                  <span className="font-mono text-lg text-primary">${o.amount?.toFixed(2)}</span>
                  <button data-testid={`print-order-${o.id}`} onClick={() => printOrder(o)}
                    className="label-mono px-3 py-1 border border-black/15 text-muted-foreground hover:border-primary hover:text-primary transition-colors inline-flex items-center gap-1.5"
                    style={{ transition: "color 0.2s, border-color 0.2s" }}><Printer size={14} /> Print</button>
                </div>
              </div>
              <div className="text-sm text-muted-foreground mb-2">{o.items?.map((i) => `${i.quantity}× ${i.name}`).join(", ")}</div>
              {o.shipping_address?.name && (
                <p data-testid={`ship-to-${o.id}`} className="text-[0.7rem] text-muted-foreground mb-3">
                  <span className="label-mono text-foreground/70">Ship to:</span> {[o.shipping_address.name, o.shipping_address.line1, [o.shipping_address.city, o.shipping_address.state, o.shipping_address.postal_code].filter(Boolean).join(", ")].filter(Boolean).join(" · ")}
                </p>
              )}
              {o.payment_status !== "paid" && o.payment_status !== "cancelled" && (
                <div className="flex flex-wrap items-center gap-3 mb-1">
                  <span className="label-mono text-primary text-[0.6rem] border border-primary/30 bg-primary/10 px-2 py-1">
                    {o.payment_method === "venmo" ? "Venmo — awaiting transfer" : o.payment_method === "zelle" ? "Zelle — awaiting transfer" : "Awaiting payment"}
                    {o.reference ? ` · ${o.reference}` : ""}
                  </span>
                  <button data-testid={`mark-paid-${o.id}`} onClick={() => markPaid(o.id)}
                    className="label-mono px-3 py-1 border border-accent/40 text-accent bg-accent/10 hover:bg-accent/20 transition-colors"
                    style={{ transition: "background-color 0.2s" }}>Mark Paid</button>
                  <button data-testid={`cancel-order-${o.id}`} onClick={() => { setCancelTarget(o); setCancelReason(defaultCancelReason(o)); }}
                    className="label-mono px-3 py-1 border border-destructive/40 text-destructive bg-destructive/10 hover:bg-destructive/20 transition-colors"
                    style={{ transition: "background-color 0.2s" }}>Cancel</button>
                </div>
              )}
              {o.payment_status === "cancelled" && (
                <div className="flex flex-wrap items-center gap-3 mb-1">
                  <p data-testid={`cancel-reason-${o.id}`} className="text-[0.7rem] text-muted-foreground">
                    <span className="label-mono text-destructive/80">Cancelled:</span>{" "}
                    {o.cancel_reason === "auto_cancel_nonpayment"
                      ? "Auto-cancelled — payment not received"
                      : (o.cancel_reason && o.cancel_reason !== "manual_cancel" ? o.cancel_reason : "Cancelled manually")}
                    {(o.cancelled_at || o.updated_at) ? ` · ${new Date(o.cancelled_at || o.updated_at).toLocaleDateString()}` : ""}
                  </p>
                  <button data-testid={`reinstate-order-${o.id}`} onClick={() => reinstateOrder(o.id)}
                    className="label-mono px-3 py-1 border border-primary/40 text-primary bg-primary/10 hover:bg-primary/20 transition-colors inline-flex items-center gap-1.5"
                    style={{ transition: "background-color 0.2s" }}><ArrowCounterClockwise size={13} /> Reinstate</button>
                </div>
              )}
              {o.payment_status === "paid" && (
                <div className="flex items-center gap-2">
                  <span className="label-mono text-muted-foreground text-[0.6rem]">Fulfillment:</span>
                  {["processing", "shipped", "delivered"].map((s) => (
                    <button key={s} onClick={() => setOrderStatus(o.id, s)}
                      className={`label-mono px-3 py-1 border transition-colors ${o.fulfillment_status === s ? "border-primary text-primary bg-primary/10" : "border-black/10 text-muted-foreground hover:border-black/30"}`}
                      style={{ transition: "color 0.2s, border-color 0.2s" }}>{s}</button>
                  ))}
                </div>
              )}
              {o.payment_status === "paid" && (
                <OrderTracking order={o} onSave={saveTracking} onResend={resendTracking} />
              )}
              {o.payment_status === "paid" && (
                <div data-testid={`label-row-${o.id}`} className="mt-3 pt-3 border-t border-black/10 flex flex-wrap items-center gap-2">
                  <span className="label-mono text-muted-foreground text-[0.6rem]">Shipping label:</span>
                  {o.label_filename ? (
                    <>
                      <a data-testid={`view-label-${o.id}`} href={`${API}/admin/orders/${o.id}/label`} target="_blank" rel="noreferrer"
                        className="label-mono text-accent text-[0.6rem] border border-accent/30 bg-accent/10 px-2 py-1 inline-flex items-center gap-1 hover:bg-accent/20 transition-colors">
                        <FilePdf size={12} weight="duotone" /> {o.label_filename}
                      </a>
                      <button data-testid={`remove-label-${o.id}`} onClick={() => removeLabel(o.id)}
                        className="label-mono text-[0.6rem] text-muted-foreground hover:text-destructive transition-colors">Remove</button>
                    </>
                  ) : (
                    <label data-testid={`upload-label-${o.id}`} className="label-mono text-[0.6rem] border border-black/15 px-3 py-1.5 text-muted-foreground hover:border-primary hover:text-primary transition-colors cursor-pointer inline-flex items-center gap-1.5">
                      {labelBusy === o.id ? "Uploading…" : <><DownloadSimple size={12} className="rotate-180" /> Upload label PDF</>}
                      <input type="file" accept="application/pdf,.pdf" className="hidden"
                        onChange={(e) => { uploadLabel(o.id, e.target.files?.[0]); e.target.value = ""; }} />
                    </label>
                  )}
                  <button data-testid={`print-label-slip-${o.id}`} onClick={() => printLabelsFor([o.id], "Label + slip ready")}
                    disabled={printingLabels}
                    className="label-mono text-[0.6rem] px-3 py-1.5 bg-primary text-black hover:bg-[#E6C766] transition-colors inline-flex items-center gap-1.5 disabled:opacity-50"
                    style={{ transition: "background-color 0.2s" }}><Printer size={12} /> Print label + slip</button>
                </div>
              )}
            </div>
            ));
          })()}
        </div>
      )}

      {editing && <ProductForm initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); loadProducts(); }} />}

      {deleteTarget && (
        <div data-testid="delete-confirm-modal" className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" onClick={() => !deleting && setDeleteTarget(null)}>
          <div className="bg-surface border border-black/10 w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-3 mb-4">
              <Warning size={24} weight="fill" className="text-destructive shrink-0 mt-0.5" />
              <div>
                <h3 className="font-head text-lg">Delete product?</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  This permanently removes <span className="text-foreground font-semibold">{deleteTarget.name}</span> from your catalog. This can't be undone.
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <button data-testid="delete-confirm-btn" onClick={del} disabled={deleting} className="flex-1 bg-destructive text-white label-mono py-3 hover:opacity-90 transition-opacity disabled:opacity-50">
                {deleting ? "Deleting…" : "Yes, delete"}
              </button>
              <button data-testid="delete-cancel-btn" onClick={() => setDeleteTarget(null)} disabled={deleting} className="flex-1 border border-black/15 label-mono py-3 hover:border-black/40 transition-colors disabled:opacity-50">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {cancelTarget && (
        <div data-testid="cancel-order-modal" className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" onClick={() => !cancelling && setCancelTarget(null)}>
          <div className="bg-surface border border-black/10 w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-3 mb-4">
              <Warning size={24} weight="fill" className="text-destructive shrink-0 mt-0.5" />
              <div>
                <h3 className="font-head text-lg">Cancel this order?</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Marks <span className="text-foreground font-semibold">{cancelTarget.customer_email || "this guest order"}</span> as cancelled. No email is sent to the customer.
                </p>
              </div>
            </div>
            <label className="label-mono text-[0.6rem] text-muted-foreground">Reason (optional)</label>
            <div data-testid="cancel-reason-presets" className="flex flex-wrap gap-2 mt-1.5 mb-2">
              {["Customer requested cancellation", "Payment not received", "Out of stock", "Suspected fraud", "Duplicate order"].map((r) => (
                <button key={r} type="button" data-testid={`cancel-preset-${r.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                  onClick={() => setCancelReason(r)}
                  className={`label-mono text-[0.55rem] px-2.5 py-1 border transition-colors ${cancelReason === r ? "border-primary text-primary bg-primary/10" : "border-black/15 text-muted-foreground hover:border-primary/50 hover:text-foreground"}`}
                  style={{ transition: "color 0.2s, border-color 0.2s" }}>{r}</button>
              ))}
            </div>
            <input data-testid="cancel-reason-input" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)}
              placeholder="e.g. Customer requested cancellation"
              className="w-full mt-1 mb-4 bg-transparent border border-black/15 px-3 py-2.5 font-mono text-sm focus:border-primary outline-none" />
            <div className="flex gap-3">
              <button data-testid="cancel-confirm-btn" onClick={confirmCancel} disabled={cancelling} className="flex-1 bg-destructive text-white label-mono py-3 hover:opacity-90 transition-opacity disabled:opacity-50">
                {cancelling ? "Cancelling…" : "Yes, cancel order"}
              </button>
              <button data-testid="cancel-abort-btn" onClick={() => setCancelTarget(null)} disabled={cancelling} className="flex-1 border border-black/15 label-mono py-3 hover:border-black/40 transition-colors disabled:opacity-50">Go back</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
